import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { useAtomValue } from "jotai";
import React from "react";
import { AppDragableBottomSheet } from "../AppDragableBottomSheet";
import { AppOverlay } from "../AppOverlay";
import SafeArea from "../SafeArea";
import { actionSheetsAtom } from "./actionSheetsAtom";

export type AppActionSheetProps<TValue> = {
  isOpen: boolean;
  options: { text: string; value: TValue }[];
  onChange: (value: TValue) => void;
  onClose: () => void;
};

export type ActionSheetInstance<TValue> = {
  id: string;
  props: AppActionSheetProps<TValue>;
};

export default function AppActionSheets() {
  const actionSheets = useAtomValue(actionSheetsAtom);

  return (
    <AppOverlay>
      <AnimatePresence>
        {actionSheets.map((actionSheet) => (
          <AppActionSheet key={actionSheet.id} {...actionSheet.props} />
        ))}
      </AnimatePresence>
    </AppOverlay>
  );
}

function AppActionSheet<TValue>(props: AppActionSheetProps<TValue>) {
  return (
    <AppDragableBottomSheet
      isOpen={props.isOpen}
      closeOnDrag={true}
      withAnimatePresence={false}
      onRequestClose={props.onClose}
    >
      <ActionSheetRoot>
        <SafeArea bottom={true}>
          <ActionSheetList>
            {props.options.map((option) => (
              <ActionSheetItem
                key={String(option.value)}
                onClick={() => {
                  props.onChange(option.value);
                  props.onClose();
                }}
              >
                {option.text}
              </ActionSheetItem>
            ))}
          </ActionSheetList>
        </SafeArea>
      </ActionSheetRoot>
    </AppDragableBottomSheet>
  );
}

const ActionSheetRoot = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActionSheetItem = styled.button`
  padding: 16px;
  font-size: 18px;
  background-color: transparent;
  border: none;
  font-weight: 600;
  color: var(--primary-color);
`;

const ActionSheetList = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;

  > ${ActionSheetItem} + ${ActionSheetItem} {
    border-top: 1px solid var(--tw-slate-200);
  }
`;
